import { createContext, useContext } from 'react'

/** Backend-emitted notice attached to the agent state.
 *
 *  - `compacted`: older messages were summarized into the assistant's working
 *    memory to free up space. The full conversation is preserved in history;
 *    only what the model sees is condensed. Normal/healthy — informational.
 *  - `trimmed`: the trimmer compressed/dropped older context so the call could fit.
 *  - `full`: even after trimming, the thread is still over budget — the LLM
 *    was NOT called. The UI should prompt the user to start a new thread.
 */
export interface ThreadStatus {
  kind: 'compacted' | 'trimmed' | 'full'
  before_tokens?: number
  after_tokens?: number
  budget_tokens: number
  dropped_turns?: number
  compressed_turns?: number
  /** Tool results stubbed in place inside kept turns (message-granular eviction). */
  evicted_tool_results?: number
  still_over_budget?: boolean
  /** ISO timestamp of when the thread was compacted (kind === 'compacted'). */
  compacted_at?: string
  /** Id of the working-memory summary; changes when a fresh compaction runs. */
  summary_id?: string
}

interface ThreadStatusContextValue {
  status: ThreadStatus | null
  /** Dismiss the currently-shown notice (in-memory only; reappears if status changes). */
  dismiss: () => void
  /** Start a fresh thread — closes the current one. Provided by the host. */
  startNewThread?: () => void
  /** Current token usage of the persisted thread (from /health). Drives the
   *  always-on context-size indicator next to the composer. */
  tokensUsed?: number
  budgetTokens?: number
}

export const ThreadStatusContext = createContext<ThreadStatusContextValue>({
  status: null,
  dismiss: () => {},
})

export function useThreadStatus(): ThreadStatusContextValue {
  return useContext(ThreadStatusContext)
}
